import React, { useCallback, useEffect, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  CircularProgress,
  MenuItem,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SaveIcon from '@mui/icons-material/Save';
import { DataGridPro } from '@mui/x-data-grid-pro';
import {
  harmonateDataGridSx,
  harmonateProceedButtonSx,
  harmonateFormFieldsSx,
  harmonateSelectMenuProps,
  harmonateTextFieldSx,
  harmonateInputLabelProps,
  harmonateDrawerAccordionSx,
  harmonateDrawerAccordionSummarySx,
  harmonateDrawerAccordionTitleSx,
  harmonateDrawerAccordionDetailsSx,
} from '../../../theme/harmonateStyles';
import { harmonateTokens as ht } from '../../../theme/harmonateTokens';

const EMPTY_MESSAGE = 'No workforce functional permissions are configured for this tenant.';

const rowKey = (row) =>
  `${row.functionalGroupCode || row.functional_group_code}::${row.functionalActionCode || row.functional_action_code}`;

const RoleFunctionalPermissions = ({ tenantRoleId, open, onSaved, onError }) => {
  const tenantId = localStorage.getItem('tenant_id') || '';
  const token = localStorage.getItem('token') || '';
  const base = () => process.env.REACT_APP_BACKEND_URL || '';

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [rows, setRows] = useState([]);
  const [hasChanges, setHasChanges] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const loadPermissions = useCallback(async () => {
    if (!tenantId || !tenantRoleId) return;
    const authHeaders = {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      'Content-Type': 'application/json',
    };
    try {
      setLoading(true);
      const resp = await fetch(
        `${base()}conductor/roles/functional-permissions/tenant/${encodeURIComponent(tenantId)}/role/${encodeURIComponent(tenantRoleId)}`,
        { headers: authHeaders },
      );
      if (!resp.ok) {
        // Missing optional workforce catalog — show empty state, not a Roles screen error.
        setRows([]);
        setHasChanges(false);
        return;
      }
      const data = await resp.json();
      setRows(
        (Array.isArray(data) ? data : []).map((item) => ({
          id: rowKey(item),
          functionalGroupCode: item.functionalGroupCode || item.functional_group_code,
          functionalGroupLabel: item.functionalGroupLabel || item.functional_group_label,
          functionalActionCode: item.functionalActionCode || item.functional_action_code,
          functionalActionLabel: item.functionalActionLabel || item.functional_action_label,
          isEnabled: item.isEnabled ?? item.is_enabled ?? false,
          configValue: item.configValue || item.config_value || '',
        })),
      );
      setHasChanges(false);
    } catch {
      setRows([]);
      setHasChanges(false);
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [tenantId, tenantRoleId, token]);

  useEffect(() => {
    if (!open) {
      setExpanded(false);
      setHasLoaded(false);
      setRows([]);
      setHasChanges(false);
      return;
    }
    // Load only when the section is opened — avoid toast/noise on drawer open.
    if (expanded && tenantRoleId) {
      loadPermissions();
    }
  }, [open, expanded, tenantRoleId, loadPermissions]);

  const handleChange = (id, field, value) => {
    setRows((prev) => prev.map((row) => (row.id === id ? { ...row, [field]: value } : row)));
    setHasChanges(true);
  };

  const handleSave = async (e) => {
    e?.stopPropagation?.();
    if (!tenantId || !tenantRoleId || rows.length === 0) return;
    try {
      setSaving(true);
      const authHeaders = {
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
        'Content-Type': 'application/json',
      };
      const payload = rows.map((row) => ({
        tenantId,
        tenantRoleId,
        functionalGroupCode: row.functionalGroupCode,
        functionalGroupLabel: row.functionalGroupLabel,
        functionalActionCode: row.functionalActionCode,
        functionalActionLabel: row.functionalActionLabel,
        isEnabled: row.isEnabled === true || row.isEnabled === 'Enabled',
        configValue: row.configValue?.trim() || null,
      }));
      const resp = await fetch(`${base()}conductor/roles/functional-permissions/bulk`, {
        method: 'POST',
        headers: authHeaders,
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const err = await resp.json().catch(() => ({}));
        throw new Error(err.error || `Save failed (${resp.status})`);
      }
      setHasChanges(false);
      onSaved?.('Functional permissions saved.');
      await loadPermissions();
    } catch (error) {
      onError?.(error.message || 'Failed to save functional permissions');
    } finally {
      setSaving(false);
    }
  };

  const columns = [
    {
      field: 'functionalGroupLabel',
      headerName: 'Functional Group',
      flex: 1.2,
      minWidth: 150,
    },
    {
      field: 'functionalActionLabel',
      headerName: 'Functional Action',
      flex: 1.2,
      minWidth: 150,
    },
    {
      field: 'isEnabled',
      headerName: 'Status',
      flex: 0.8,
      minWidth: 120,
      renderCell: (params) => (
        <TextField
          select
          size="small"
          value={params.row.isEnabled ? 'Enabled' : 'Disabled'}
          onChange={(e) => handleChange(params.row.id, 'isEnabled', e.target.value === 'Enabled')}
          InputLabelProps={harmonateInputLabelProps}
          SelectProps={{ MenuProps: harmonateSelectMenuProps }}
          sx={{ minWidth: 110, ...harmonateTextFieldSx }}
        >
          <MenuItem value="Enabled" sx={{ fontSize: '0.875rem' }}>Enabled</MenuItem>
          <MenuItem value="Disabled" sx={{ fontSize: '0.875rem' }}>Disabled</MenuItem>
        </TextField>
      ),
    },
    {
      field: 'configValue',
      headerName: 'Value / Configuration',
      flex: 1.2,
      minWidth: 140,
      renderCell: (params) => (
        <TextField
          size="small"
          placeholder="Optional"
          value={params.row.configValue || ''}
          onChange={(e) => handleChange(params.row.id, 'configValue', e.target.value)}
          fullWidth
          InputLabelProps={harmonateInputLabelProps}
          sx={harmonateTextFieldSx}
        />
      ),
    },
  ];

  return (
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={() => setExpanded((prev) => !prev)}
      sx={harmonateDrawerAccordionSx}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon sx={{ color: ht.text }} />} sx={harmonateDrawerAccordionSummarySx}>
        <Typography sx={harmonateDrawerAccordionTitleSx}>Functional Permission Management</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {hasChanges && rows.length > 0 ? (
            <Button
              onClick={handleSave}
              variant="contained"
              size="small"
              startIcon={<SaveIcon />}
              disabled={saving}
              sx={{ ...harmonateProceedButtonSx, mr: 1 }}
            >
              {saving ? 'Saving…' : 'Save'}
            </Button>
          ) : null}
        </Box>
      </AccordionSummary>
      <AccordionDetails sx={{ ...harmonateDrawerAccordionDetailsSx, height: 360, overflow: 'hidden', ...harmonateFormFieldsSx }}>
        {loading || !hasLoaded ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}>
            <CircularProgress sx={{ color: ht.blue }} />
          </Box>
        ) : rows.length === 0 ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200, px: 2 }}>
            <Typography variant="body2" sx={{ color: ht.fadedText, textAlign: 'center' }}>
              {EMPTY_MESSAGE}
            </Typography>
          </Box>
        ) : (
          <>
            <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', height: 320 }}>
              <DataGridPro
                columnResizeMode="onEnd"
                resizable
                rows={rows}
                columns={columns}
                rowHeight={48}
                disableSelectionOnClick
                getRowId={(row) => row.id}
                sx={{ ...harmonateDataGridSx, flex: 1 }}
                pagination
                pageSize={25}
              />
            </Box>
            <Typography variant="caption" sx={{ px: 2, py: 1, color: ht.text }}>
              Enable a workforce action here first; workflow steps then control which role owns each step in Scheduling Settings.
            </Typography>
          </>
        )}
      </AccordionDetails>
    </Accordion>
  );
};

export default RoleFunctionalPermissions;
